import CollapsibleSidebarLayout from "components/collapsible-sidebar-layout/collapsible-sidebar-layout";
import { useEffect, useMemo, useRef, useState } from "react";
import ErrorBoundary from "components/error-boundary";
import { useParams } from "react-router-dom";
import { Spin } from "antd";
import { AgGridReact } from "ag-grid-react";
import {
  CustomReportsProvider,
  useCustomReports
} from "./context/custom-reports-provider";

import {
  DatasetSidebarContent,
  DatasetSidebarHeader
} from "./components/dataset-sidebar";
import ResultsPanel from "./components/results-panel";
import ReportsPageHeader from "./components/reports-page-header";
import useManageConfigs from "./hooks/manageConfigs";
import usePageTitle from "hooks/use-page-title";
import "./custom-report-page.scss";

function CustomReportPageContent() {
  const { reportId, projectId } = useParams() as {
    reportId: string;
    projectId: string;
  };

  const { datasetConfig, permissions, detail } = useCustomReports();

  usePageTitle(detail?.name);

  const { onUpdateDatasetConfig, onUpdateProjectReport, onUpdateColumnConfig } =
    useManageConfigs(reportId);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const gridRef = useRef<AgGridReact<any>>(null);

  useEffect(() => {
    if (permissions.canConfigureCustomReports) {
      setIsSidebarOpen(true);
    }
  }, [permissions.canConfigureCustomReports]);

  const showExportOptions = useMemo(() => {
    const submittalDisplayType = datasetConfig?.linkedEntity.find(
      (entity: any) => entity.feature_type_id === 1
    )?.display_type;
    const groupByField = datasetConfig?.groupByField;
    return submittalDisplayType === "inline" || groupByField !== "none";
  }, [datasetConfig]);

  if (!datasetConfig) {
    return (
      <div
        className="flex justify-center items-center h-full"
        data-testid="custom-report-loading"
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="custom-report-page">
      <div
        className="w-full border-solid border-b border-0 border-gray-200"
        data-testid="custom-report-header"
      >
        <ErrorBoundary>
          <ReportsPageHeader
            onUpdateProjectReport={onUpdateProjectReport}
            disable={!permissions.canConfigureCustomReports}
            showExportOptions={showExportOptions}
            gridRef={gridRef}
          />
        </ErrorBoundary>
      </div>
      <CollapsibleSidebarLayout
        sidebarState={[isSidebarOpen, setIsSidebarOpen]}
        sidebarHeader={
          <DatasetSidebarHeader
            title="Dataset"
            onClose={() => setIsSidebarOpen(false)}
          />
        }
        sidebarContent={
          <ErrorBoundary>
            <DatasetSidebarContent
              onUpdateDatasetConfig={onUpdateDatasetConfig}
              onUpdateProjectReport={onUpdateProjectReport}
            />
          </ErrorBoundary>
        }
        mainContent={
          <ErrorBoundary>
            <ResultsPanel
              sidebarState={[isSidebarOpen, setIsSidebarOpen]}
              onUpdateColumnConfig={onUpdateColumnConfig}
              onUpdateProjectReport={onUpdateProjectReport}
              projectId={projectId}
              reportId={reportId}
              gridRef={gridRef}
            />
          </ErrorBoundary>
        }
      />
    </div>
  );
}

function CustomReportPage() {
  return (
    <CustomReportsProvider>
      <ErrorBoundary>
        <CustomReportPageContent />
      </ErrorBoundary>
    </CustomReportsProvider>
  );
}

export default CustomReportPage;
